import { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import {
  findSensitiveWords,
  hasSensitiveWords,
  initializeSensitiveWords,
  isSensitiveWordsInitialized,
} from '../utils/sensitiveWords';
import {
  initializeSensitiveWordsFromConfig,
  SensitiveWordsLanguage,
} from '../utils/sensitiveWordsLoader';

export interface UseSensitiveCheckOptions {
  /**
   * 初始文本
   */
  initialText?: string;
  /**
   * 是否启用敏感词检查，默认true
   */
  enabled?: boolean;
  /**
   * 最多返回的敏感词数量，默认3
   */
  maxResults?: number;
  /**
   * 敏感词列表，如果提供则自动初始化
   */
  wordList?: string[];
  /**
   * 是否从配置文件加载敏感词，如果为true则忽略wordList参数
   * 可以指定要加载的语言列表，默认加载所有语言 ['zh', 'en', 'es']
   */
  loadFromConfig?: boolean | SensitiveWordsLanguage[];
  /**
   * 是否实时检查（输入时立即检查），默认true
   */
  realtimeCheck?: boolean;
  /**
   * 错误提示模板函数
   * @param words 检测到的敏感词列表
   * @returns 格式化后的错误提示文本
   */
  errorMessageTemplate?: (words: string[]) => string;
}

export interface UseSensitiveCheckResult {
  /**
   * 当前文本值
   */
  text: string;
  /**
   * 设置文本值
   */
  setText: (text: string) => void;
  /**
   * 检测到的敏感词列表
   */
  errorWords: string[];
  /**
   * 格式化后的错误提示消息（如果提供了errorMessageTemplate）
   */
  errorMessage: string | null;
  /**
   * 是否通过检查（没有敏感词）
   */
  isPass: boolean;
  /**
   * 输入处理函数，可直接绑定到input/textarea的onChange事件
   */
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * 手动检查文本
   * @param inputText 要检查的文本，如果不提供则检查当前text
   * @returns 是否通过检查
   */
  checkText: (inputText?: string) => boolean;
  /**
   * 清理敏感词状态（清空errorWords）
   */
  clearError: () => void;
}

/**
 * 敏感词检查 Hook
 * 
 * 使用示例：
 * ```tsx
 * import { useTranslation } from 'react-i18next';
 * 
 * const { t } = useTranslation();
 * const { text, errorWords, errorMessage, isPass, handleChange, checkText } = useSensitiveCheck({
 *   initialText: '',
 *   wordList: ['敏感词1', '敏感词2'],
 *   realtimeCheck: true,
 *   // 使用提示模板支持多语言
 *   errorMessageTemplate: (words) => {
 *     return t('sensitiveWords.error', { words: words.join('、') });
 *   }
 * });
 * 
 * <textarea
 *   value={text}
 *   onChange={handleChange}
 *   maxLength={300}
 * />
 * 
 * {!isPass && (
 *   <p>{errorMessage || `包含敏感词：${errorWords.join('、')}`}</p>
 * )}
 * ```
 */
export const useSensitiveCheck = (options: UseSensitiveCheckOptions = {}): UseSensitiveCheckResult => {
  const {
    initialText = '',
    enabled = true,
    maxResults = 3,
    wordList,
    loadFromConfig,
    realtimeCheck = true,
    errorMessageTemplate,
  } = options;

  const [text, setText] = useState<string>(initialText);
  const [errorWords, setErrorWords] = useState<string[]>([]);
  const initializedRef = useRef<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // 计算错误提示消息
  const errorMessage: string | null = errorWords.length > 0 && errorMessageTemplate
    ? errorMessageTemplate(errorWords)
    : null;

  // 初始化敏感词列表
  useEffect(() => {
    const initWords = async () => {
      if (loadFromConfig) {
        // 从配置文件加载
        setLoading(true);
        try {
          const languages: SensitiveWordsLanguage[] = Array.isArray(loadFromConfig) 
            ? loadFromConfig 
            : ['zh', 'en', 'es'];
          await initializeSensitiveWordsFromConfig(languages);
          initializedRef.current = true;
        } catch (error) {
          console.error('加载敏感词配置文件失败:', error);
        } finally {
          setLoading(false);
        }
      } else if (wordList && wordList.length > 0) {
        // 从提供的列表初始化
        initializeSensitiveWords(wordList);
        initializedRef.current = true;
      }
    };

    initWords();
  }, [wordList, loadFromConfig]);

  // 检查文本函数
  const checkText = useCallback(
    (inputText?: string): boolean => {
      if (!enabled) {
        setErrorWords([]);
        return true;
      }

      // 检查是否已初始化
      if (!isSensitiveWordsInitialized()) {
        // 如果没有初始化，默认通过检查
        setErrorWords([]);
        return true;
      }

      const textToCheck = inputText !== undefined ? inputText : text;

      if (!textToCheck || textToCheck.trim().length === 0) {
        setErrorWords([]);
        return true;
      }

      const found = findSensitiveWords(textToCheck.trim(), maxResults);

      if (found.length > 0) {
        setErrorWords(found);
        return false;
      } else {
        setErrorWords([]);
        return true;
      }
    },
    [enabled, text, maxResults]
  );

  // 输入处理函数
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setText(newText);

      if (realtimeCheck) {
        checkText(newText);
      }
    },
    [realtimeCheck, checkText]
  );

  // 清理错误状态
  const clearError = useCallback(() => {
    setErrorWords([]);
  }, []);

  // 初始加载时检查一次（仅在realtimeCheck为true时）
  useEffect(() => {
    if (realtimeCheck && initializedRef.current) {
      checkText();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPass = errorWords.length === 0;

  return {
    text,
    setText,
    errorWords,
    errorMessage,
    isPass,
    handleChange,
    checkText,
    clearError,
  };
};

